import { Metric } from "@/entities/Finance/Finance.model";
import { HydratedMetric } from "./HydratedMetric";
import { PeriodResolution } from "./Metrics.types";
import { PeriodRange } from "./PeriodRange";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";
import { METRIC_CONFIG } from "./METRIC_CONFIG";

export type MetricCode = AccountLabel | FinanceLabField;
export type MetricCodeWithFallbacks = MetricCode | MetricCode[];

export class Metrics {
    readonly metrics: {
        monthly: Map<MetricCode, HydratedMetric>;
        quarterly: Map<MetricCode, HydratedMetric>;
        yearly: Map<MetricCode, HydratedMetric>;
    };

    constructor(metrics: {
        monthly: Metric[];
        quarterly: Metric[];
        yearly: Metric[];
    }) {
        this.metrics = {
            monthly: new Map(
                metrics.monthly
                    .filter(
                        (metric) =>
                            metric.info.derived.label &&
                            METRIC_CONFIG[metric.info.derived.label],
                    )
                    .map((metric) => {
                        const hydrated = new HydratedMetric(
                            metric,
                            PeriodResolution.MONTHLY,
                        );

                        return [hydrated.code, hydrated];
                    }),
            ),

            quarterly: new Map(
                metrics.quarterly
                    .filter(
                        (metric) =>
                            metric.info.derived.label &&
                            METRIC_CONFIG[metric.info.derived.label],
                    )
                    .map((metric) => {
                        const hydrated = new HydratedMetric(
                            metric,
                            PeriodResolution.QUARTERLY,
                        );
                        return [hydrated.code, hydrated];
                    }),
            ),
            yearly: new Map(
                metrics.yearly
                    .filter(
                        (metric) =>
                            metric.info.derived.label &&
                            METRIC_CONFIG[metric.info.derived.label],
                    )
                    .map((metric) => {
                        const hydrated = new HydratedMetric(
                            metric,
                            PeriodResolution.YEARLY,
                        );
                        return [hydrated.code, hydrated];
                    }),
            ),
        };
    }

    static fromHydratedMetrics(
        monthly: HydratedMetric[],
        quarterly: HydratedMetric[],
        yearly: HydratedMetric[],
    ): Metrics {
        return new Metrics({
            monthly: monthly.map((metric) => metric.rawData),
            quarterly: quarterly.map((metric) => metric.rawData),
            yearly: yearly.map((metric) => metric.rawData),
        });
    }

    private matchesCode(
        code: MetricCode,
        codes?: MetricCodeWithFallbacks[],
    ): boolean {
        if (!codes) return true;
        return codes.some((codeOrFallbacks) =>
            Array.isArray(codeOrFallbacks)
                ? codeOrFallbacks.includes(code)
                : codeOrFallbacks === code,
        );
    }

    getGlobalPeriodRange(
        resolution: PeriodResolution,
        codes?: MetricCodeWithFallbacks[],
    ): PeriodRange | null {
        const allPeriods = [...this.metrics[resolution].values()]
            .filter((metric) => metric.periodRange !== null)
            .filter((metric) => this.matchesCode(metric.code, codes))
            .map((metric) => metric.periodRange!.getAllPeriods(resolution))
            .flat();
        return PeriodRange.fromArray(allPeriods);
    }

    getMetric(
        code: MetricCodeWithFallbacks,
        resolution: PeriodResolution,
    ): HydratedMetric | null {
        const codes = Array.isArray(code) ? code : [code];
        for (const c of codes) {
            const metric = this.metrics[resolution].get(c);
            if (metric) return metric;
        }
        return null;
    }

    setMetric(metric: HydratedMetric): void {
        this.metrics[metric.resolution].set(metric.code, metric);
    }

    getMetrics(codes: MetricCodeWithFallbacks[]): Metrics {
        return Metrics.fromHydratedMetrics(
            [...this.metrics.monthly.values()].filter((metric) =>
                this.matchesCode(metric.code, codes),
            ),
            [...this.metrics.quarterly.values()].filter((metric) =>
                this.matchesCode(metric.code, codes),
            ),
            [...this.metrics.yearly.values()].filter((metric) =>
                this.matchesCode(metric.code, codes),
            ),
        );
    }
}
